import React, { useCallback, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, EffectCoverflow } from 'swiper/modules';
import DocumentsModal from '../../components/DocumentsModal/DocumentsModal';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-coverflow';
import './AboutUsPage.css';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const AboutUsPage: React.FC = () => {
  const { t } = useTranslation();
  const [isDocumentsModalOpen, setIsDocumentsModalOpen] = useState(false);

  const featuredVideoId = 'crash_bad_saarow';
  const videoSlides = [
    {
      id: 'movie',
      src: `${process.env.PUBLIC_URL}/images/movies/movie.mov`,
    },
    {
      id: featuredVideoId,
      src: `${process.env.PUBLIC_URL}/images/movies/crash_bad_saarow.mov`,
    },
    {
      id: 'movie2',
      src: `${process.env.PUBLIC_URL}/images/movies/movie2.mov`,
    },
  ];
  const desiredInitialSlide = Math.max(
    videoSlides.findIndex((video) => video.id === featuredVideoId),
    0
  );

  const pauseVideos = useCallback((exclude?: HTMLVideoElement | null) => {
    if (typeof document === 'undefined') {
      return;
    }

    const videos = document.querySelectorAll<HTMLVideoElement>('[data-about-video="true"]');
    videos.forEach((video) => {
      if (exclude && video === exclude) {
        return;
      }

      if (!video.paused) {
        video.pause();
      }
    });
  }, []);

  const handleVideoPlay = useCallback((event: React.SyntheticEvent<HTMLVideoElement>) => {
    pauseVideos(event.currentTarget);
  }, [pauseVideos]);

  const handleSlideChange = useCallback(() => {
    pauseVideos();
  }, [pauseVideos]);

  return (
    <section id="about-us" className="py-12 px-4 bg-white">
      <div className="max-w-6xl mx-auto">
        <motion.h2 
          className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-10 text-center"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          {t('navigation.about', 'O Nas')}
        </motion.h2>
        
        <motion.div 
          className="mb-6 sm:mb-8 px-4 md:px-0"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-base sm:text-lg text-gray-700 mb-4 text-center">
            {t('about.description', 'Wodne Ochotnicze Pogotowie Ratunkowe Oddział Miejski w Chodzieży jest jednostką administracyjną Wielkopolskiego WOPR od roku 2009. W swoich szeregach posiadamy przeszkolonych ratowników wodnych, ratowników KPP, ratowników medycznych, płetwonurków oraz sterników motorowodnych. W strukturach Chodzieskiego WOPR istnieje grupa Water Rescue Team zajmująca się zabezpieczaniem zawodów motorowodnych rangi krajowej oraz międzynarodowej.')}
          </p>
          <div className="flex justify-center mt-6">
            <button
              onClick={() => setIsDocumentsModalOpen(true)}
              className="px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              {t('about.view_documents', 'Zobacz dokumenty')}
            </button>
          </div>
        </motion.div>

        <div className="my-6 md:my-8 border-t border-gray-300"></div>

        <motion.div 
          className="w-full md:w-4/5 lg:w-3/4 mx-auto swiper-container"
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Swiper
            loop={true}
            effect={'coverflow'}
            grabCursor={true}
            centeredSlides={true}
            initialSlide={desiredInitialSlide}
            slidesPerView={'auto'}
            slideToClickedSlide={true}
            coverflowEffect={{
              rotate: 20,
              stretch: 0,
              depth: 200,
              modifier: 1,
              slideShadows: true,
            }}
            modules={[EffectCoverflow, Navigation, Pagination]}
            navigation
            pagination={{ clickable: true }}
            className="mySwiper"
            onSlideChange={handleSlideChange}
          >
            {videoSlides.map((video) => (
              <SwiperSlide key={video.id}>
                <div className="relative w-full aspect-video rounded-lg overflow-hidden">
                  <video
                    className="w-full h-full object-cover"
                    playsInline
                    loop
                    preload="auto"
                    controls
                    data-about-video="true"
                    onPlay={handleVideoPlay}
                  >
                    <source src={video.src} />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </motion.div>

        <DocumentsModal
          isOpen={isDocumentsModalOpen}
          onClose={() => setIsDocumentsModalOpen(false)}
        />
      </div>
    </section>
  );
};

export default AboutUsPage;
